import { useState, useEffect } from "react";
import axios from "axios";
import { MiturLogoImage, AuthBackgroundImage, FormSchema } from "./LoginConstants";
import {
    LogoImage,
    Image,
    LoginContainer,
    LeftPanelContainer,
    Title,
    FlexStartContainer,
    LinkText,
    BodyText,
    FooterContainer,
    TextFieldContainer,
    TextError,
    CheckboxContainer,
} from "./styles/LoginStyles";
import { Checkbox, FormControlLabel, Typography } from "@mui/material";
import { StyledButton, SmallHeightDivider, MediumHeightDivider, StyleRadio } from "../../../theme/Styles";
import COLORS from "../../../theme/Colors";
import { useFormik } from "formik";
import { useHistory } from "react-router";
import TextField from "../../../components/TextField/TextField";
import { useDispatch, useSelector } from "react-redux";
import { AuthLogin } from "../../../redux/actions/AuthActions";
import { HideGlobalLoading, ShowGlobalLoading } from "../../../redux/actions/UiActions";
import apiCall from "../../../services/ApiServerCall";
import LocalStorageService from "../../../services/LocalStorageService";
import { getUser, registerLoggedUserInServiceBackend, sendLoginOtp, verifyLoginOtp } from "../../../api/Auth";
import { cleanStringFromNumbers } from "../../../utilities/functions/NumberUtil";
import { useQuery } from "react-query";
import userLogo from "../../../assets/images/user.png";
import { removeGuionFromString } from "../../../utilities/functions/StringUtil";
import SecurityIcon from "@mui/icons-material/Security";

function Login() {
    const history = useHistory();
    const dispatch = useDispatch();
    const { authenticated } = useSelector((state) => state.authReducer);

    const [errorMessage, setErrorMessage] = useState("");
    const [showOtpScreen, setShowOtpScreen] = useState(false);
    const [otpCode, setOtpCode] = useState("");
    const [otpError, setOtpError] = useState("");
    const [isSendingOtp, setIsSendingOtp] = useState(false);
    const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
    const [tempLoginData, setTempLoginData] = useState(null);

    const { refetch } = useQuery(["userData"], () => getUser(), { enabled: false });

    const formik = useFormik({
        initialValues: {
            id: LocalStorageService.getItem("remembered_id") || "",
            password: "",
            rememberMe: !!LocalStorageService.getItem("remembered_id"),
        },
        validationSchema: FormSchema,
        onSubmit: (values) => {
            handleLogin(values);
        },
    });

    const handleLogin = async (formData) => {
        try {
            dispatch(ShowGlobalLoading("Iniciando sesión"));
            setErrorMessage("");
            let response = await apiCall().post("/auth/login", {
                citizen_id: removeGuionFromString(formData.id),
                password: formData.password,
            });
            if (response.data?.success) {
                const token = response.data?.payload.token;
                const profileImg = response.data?.payload.profile_img == null || response.data?.payload.profile_img === "N/A"
                    ? userLogo
                    : response.data?.payload.profile_img;

                LocalStorageService.setItem("token", token);
                
                let userResponse;
                try {
                    userResponse = await getUser();
                } catch (e) {
                    LocalStorageService.removeItem("token");
                    dispatch(HideGlobalLoading());
                    setErrorMessage("Error al obtener la información de usuario");
                    return;
                }

                const userId = userResponse.payload.citizen_id;
                const check2FA = (val) => val === true || val === 1 || val === 'true' || val === '1';
                const is2FAEnabled = LocalStorageService.getItem(`two_factor_enabled_${userId}`) === "true" ||
                                     check2FA(userResponse.payload.two_factor) ||
                                     check2FA(userResponse.payload.two_factor_enabled);

                if (is2FAEnabled) {
                    LocalStorageService.removeItem("token");
                    
                    setTempLoginData({
                        token,
                        profileImg,
                        userResponse,
                        formData
                    });

                    setIsSendingOtp(true);
                    try {
                        await sendLoginOtp(token);
                        setOtpCode("");
                        setOtpError("");
                        setShowOtpScreen(true);
                    } catch (error) {
                        setErrorMessage("Error al enviar el código OTP de seguridad.");
                    } finally {
                        setIsSendingOtp(false);
                        dispatch(HideGlobalLoading());
                    }
                } else {
                    LocalStorageService.setItem("profile_img", profileImg);
                    LocalStorageService.setItem("user_cedula", userResponse.payload.citizen_id);
                    LocalStorageService.setItem("user_primary_email", userResponse.payload.email);
                    refetch();
                    
                    const requestData = {
                        id: userResponse.payload.citizen_id,
                        mail: userResponse.payload.email,
                        name: userResponse.payload.name,
                        surname: userResponse.payload.first_last_name,
                        secsurname: userResponse.payload.second_last_name,
                        phone: cleanStringFromNumbers(userResponse.payload.phone),
                        city: userResponse.payload.municipality,
                        created_date: {
                            date: "2019-05-15 04:54:47.000000",
                            timezone_type: 3,
                            timezone: "UTC",
                        },
                    };
                    await registerLoggedUserInServiceBackend(requestData);
                    
                    if (formData.rememberMe) {
                        LocalStorageService.setItem("remembered_id", formData.id);
                    } else {
                        LocalStorageService.removeItem("remembered_id");
                    }

                    dispatch(
                        AuthLogin({
                            authenticated: true,
                            profileImg: response.data.payload.profile_img,
                            rolBase: userResponse.payload.rol_base,
                            companyID: userResponse.payload.company_id,
                            citizenID: userResponse.payload.citizen_id,
                        })
                    );
                    dispatch(HideGlobalLoading());
                }
            } else {
                dispatch(HideGlobalLoading());
                setErrorMessage(response.data?.msg);
            }
        } catch (error) {
            LocalStorageService.removeItem("token");
            LocalStorageService.removeItem("profile_img");
            setErrorMessage("Ha ocurrido un error, favor contacte a soporte");
            dispatch(HideGlobalLoading());
        }
    };

    const handleVerifyLoginOtp = async () => {
        if (!tempLoginData || !otpCode) {
            setOtpError("Por favor introduzca el código OTP");
            return;
        }
        setIsVerifyingOtp(true);
        setOtpError("");
        try {
            const result = await verifyLoginOtp(otpCode, tempLoginData.token);
            if (result?.success) {
                dispatch(ShowGlobalLoading("Iniciando sesión"));
                
                LocalStorageService.setItem("token", tempLoginData.token);
                LocalStorageService.setItem("profile_img", tempLoginData.profileImg);
                LocalStorageService.setItem("user_cedula", tempLoginData.userResponse.payload.citizen_id);
                LocalStorageService.setItem("user_primary_email", tempLoginData.userResponse.payload.email);
                refetch();
                
                const requestData = {
                    id: tempLoginData.userResponse.payload.citizen_id,
                    mail: tempLoginData.userResponse.payload.email,
                    name: tempLoginData.userResponse.payload.name,
                    surname: tempLoginData.userResponse.payload.first_last_name,
                    secsurname: tempLoginData.userResponse.payload.second_last_name,
                    phone: cleanStringFromNumbers(tempLoginData.userResponse.payload.phone),
                    city: tempLoginData.userResponse.payload.municipality,
                    created_date: {
                        date: "2019-05-15 04:54:47.000000",
                        timezone_type: 3,
                        timezone: "UTC",
                    },
                };
                await registerLoggedUserInServiceBackend(requestData);
                
                if (tempLoginData.formData.rememberMe) {
                    LocalStorageService.setItem("remembered_id", tempLoginData.formData.id);
                } else {
                    LocalStorageService.removeItem("remembered_id");
                }

                dispatch(
                    AuthLogin({
                        authenticated: true,
                        profileImg: tempLoginData.profileImg,
                        rolBase: tempLoginData.userResponse.payload.rol_base,
                        companyID: tempLoginData.userResponse.payload.company_id,
                        citizenID: tempLoginData.userResponse.payload.citizen_id,
                    })
                );
                dispatch(HideGlobalLoading());
            } else {
                setOtpError(result?.msg || "Código OTP inválido.");
            }
        } catch (error) {
            setOtpError(error.response?.data?.msg || "Código OTP inválido o vencido.");
        } finally {
            setIsVerifyingOtp(false);
        }
    };

    const handleResendLoginOtp = async () => {
        if (!tempLoginData) return;
        setIsSendingOtp(true);
        setOtpError("");
        try {
            await sendLoginOtp(tempLoginData.token);
            setOtpError("Código reenviado con éxito.");
        } catch (error) {
            setOtpError("Error al reenviar el código OTP.");
        } finally {
            setIsSendingOtp(false);
        }
    };

    useEffect(() => {
        if (authenticated) {
            const redirectUrl = LocalStorageService.getItem("redirect_after_login");
            if (redirectUrl) {
                LocalStorageService.removeItem("redirect_after_login");
                history.push(redirectUrl);
            } else {
                history.push("/app/mi-escritorio");
            }
        }
    }, [authenticated, history]);

    if (showOtpScreen) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 27px)', width: '100%', overflowY: 'auto' }}>
                <LoginContainer style={{ flex: 1, overflowY: 'auto' }}>
                    <LeftPanelContainer>
                        <LogoImage src={MiturLogoImage} />
                        <SmallHeightDivider />
                        
                        <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0 10px 0' }}>
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                width: '64px', 
                                height: '64px', 
                                borderRadius: '50%', 
                                backgroundColor: 'rgba(32, 42, 93, 0.05)', 
                                border: '2px solid #202a5d' 
                            }}>
                                <SecurityIcon sx={{ fontSize: '32px', color: '#202a5d' }} />
                            </div>
                        </div>

                        <Title style={{ textAlign: 'center', marginBottom: '8px' }}>Verificación de Doble Factor (2FA)</Title>
                        
                        <div style={{ 
                            backgroundColor: '#F8FAFC', 
                            border: '1px solid #E2E8F0', 
                            borderRadius: '12px', 
                            padding: '16px', 
                            width: '100%', 
                            textAlign: 'center',
                            boxSizing: 'border-box',
                            marginBottom: '24px',
                            marginTop: '12px'
                        }}>
                            <BodyText style={{ margin: '0 0 6px 0', fontSize: '13px', color: '#64748B' }}>
                                Se ha enviado un código de seguridad de 6 dígitos a su correo registrado:
                            </BodyText>
                            <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#192873', fontFamily: 'Roboto, sans-serif' }}>
                                {tempLoginData?.userResponse?.payload?.email}
                            </Typography>
                        </div>
                        
                        <FlexStartContainer style={{ width: '100%' }} onSubmit={(e) => { e.preventDefault(); handleVerifyLoginOtp(); }}>
                            <TextFieldContainer style={{ width: '100%', marginBottom: '16px' }}>
                                <TextField
                                    type="text"
                                    title="Código de Seguridad OTP"
                                    placeholder="Ej. 123456"
                                    id="otpCode"
                                    value={otpCode}
                                    onChange={(e) => setOtpCode(e.target.value)}
                                />
                            </TextFieldContainer>
                            
                            {otpError && (
                                <div style={{ 
                                    backgroundColor: '#FEF2F2', 
                                    border: '1px solid #FCA5A5', 
                                    borderRadius: '8px', 
                                    padding: '10px', 
                                    width: '100%', 
                                    textAlign: 'center',
                                    boxSizing: 'border-box',
                                    marginBottom: '16px'
                                }}>
                                    <TextError style={{ margin: 0, fontWeight: 'bold', color: '#DC2626' }}>{otpError}</TextError>
                                </div>
                            )}
                            
                            <StyledButton 
                                type="submit"
                                disabled={isVerifyingOtp || !otpCode}
                                style={{ width: 'auto', minWidth: '250px', alignSelf: 'center', padding: '12px 40px', borderRadius: '10px' }}
                            >
                                {isVerifyingOtp ? "Verificando..." : "Verificar y Continuar"}
                            </StyledButton>
                            
                            <SmallHeightDivider />
                            
                            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', width: '100%', marginTop: '16px' }}>
                                <LinkText 
                                    to="#" 
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleResendLoginOtp();
                                    }}
                                    style={{ textDecoration: 'none', color: COLORS.primary, fontWeight: 'bold' }}
                                >
                                    Reenviar código
                                </LinkText>
                                <span style={{ color: '#ccc' }}>|</span>
                                <LinkText 
                                    to="#" 
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setShowOtpScreen(false);
                                        setTempLoginData(null);
                                    }}
                                    style={{ textDecoration: 'none', color: COLORS.secondary }}
                                >
                                    Cancelar
                                </LinkText>
                            </div>
                        </FlexStartContainer>
                    </LeftPanelContainer>
                    <Image
                        style={{
                            backgroundImage: `url(${AuthBackgroundImage})`,
                        }}
                    />
                </LoginContainer>
                <mitur-footer variant="minimal"></mitur-footer>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 27px)', width: '100%', overflowY: 'auto' }}>
            <LoginContainer style={{ flex: 1, overflowY: 'auto' }}>
                <LeftPanelContainer>
                    <LogoImage src={MiturLogoImage} />
                    <SmallHeightDivider />
                    <Title>Iniciar Sesión</Title>
                    <SmallHeightDivider />
                    <SmallHeightDivider />
                    <FlexStartContainer onSubmit={(e) => { e.preventDefault(); formik.handleSubmit(); }}>
                        <TextFieldContainer>
                            <TextField
                                type="text"
                                title={"Cédula/Pasaporte/RNC"}
                                placeholder="Cédula, Pasaporte o RNC"
                                id="id"
                                value={formik.values.id}
                                onChange={formik.handleChange}
                                error={formik.touched.id && Boolean(formik.errors.id)}
                                helperText={formik.touched.id && formik.errors.id}
                            />
                        </TextFieldContainer>
                        <TextFieldContainer>
                            <TextField
                                type="password"
                                placeholder="Contraseña"
                                id="password"
                                title={"Contraseña"}
                                value={formik.values.password}
                                onChange={formik.handleChange}
                                error={formik.touched.password && Boolean(formik.errors.password)}
                                helperText={formik.touched.password && formik.errors.password}
                            />
                        </TextFieldContainer>
                        
                        <CheckboxContainer>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        id="rememberMe"
                                        name="rememberMe"
                                        checked={formik.values.rememberMe}
                                        onChange={formik.handleChange}
                                        sx={{
                                            color: COLORS.secondary,
                                            '&.Mui-checked': { color: COLORS.primary },
                                        }}
                                    />
                                }
                                label={<BodyText style={{ fontSize: '13px' }}>Recordar sesión</BodyText>}
                            />
                            <LinkText to="/requestPassword" style={{ textDecoration: 'none', color: COLORS.secondary, fontSize: '13px' }}>
                                ¿Olvidaste tu contraseña?
                            </LinkText>
                        </CheckboxContainer>

                        <TextError>{errorMessage}</TextError>
                        <SmallHeightDivider />
                        
                        <StyledButton 
                            type="submit"
                            style={{ width: 'auto', minWidth: '250px', alignSelf: 'center', padding: '12px 40px' }}
                        >
                            Iniciar sesión
                        </StyledButton>
                        
                        <SmallHeightDivider />
                        <BodyText style={{ alignSelf: 'center' }}>
                            ¿No tienes usuario?
                            <LinkText
                                to="/registro"
                                style={{
                                    color: COLORS.primary,
                                    textDecoration: 'none',
                                    fontWeight: 'bold',
                                    marginLeft: '5px'
                                }}
                            >
                                Regístrate aquí
                            </LinkText>
                        </BodyText>
                    </FlexStartContainer>
                </LeftPanelContainer>
                <Image
                    style={{
                        backgroundImage: `url(${AuthBackgroundImage})`,
                    }}
                />
            </LoginContainer>
            <mitur-footer variant="minimal"></mitur-footer>
        </div>
    );
}

export default Login;
